/*
  assets/b-collection.js  (#443 Stage 2)  Custom element <b-collection> for sections/b-collection.

  Progressive enhancement only: the section is fully server-rendered (products, filters, prices,
  numbered page links), so search engines, AI crawlers and shoppers without JavaScript get the
  whole page. With this script:
  - a filter change (b-filters:change), a removed chip, Clear all and page links fetch just this
    section through Shopify's Section Rendering API (?section_id=) and swap the results and the
    filter form in place; the address bar follows (pushState) and Back/Forward re-render.
  There is no Load more (#508: numbered page links only, for SEO).
  Any fetch failure falls back to a normal page load at the same URL.
  Emits `b-collection:update` (bubbles) with detail.url after each in-place update.
*/
if (!customElements.get('b-collection')) {
  class BCollection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      if (!this.sectionId) return;
      this.setAttribute('data-enhanced', '');
      // A Filters block set to Metafield filters (#541) owns filtering, sorting, paging and Back
      // through assets/b-product-filters.js, so this element leaves them alone.
      if (this.dataset.filterSource === 'product_fields') return;

      this.onFilterChange = (event) => {
        event.preventDefault();
        this.navigate(this.urlFor(event.detail.params), true);
      };
      this.onClick = (event) => {
        const link = event.target.closest('a[href]');
        if (!link || !this.contains(link)) return;
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (link.closest('.b-filters__drawer-head, .b-filters__active, .b-collection__pagination, .b-collection__empty')) {
          event.preventDefault();
          // A new page starts at the top of the products, not where its page link was.
          this.navigate(this.keepView(link.href), true, !!link.closest('.b-collection__pagination'));
        }
      };
      // Re-render whenever Back/Forward lands on a URL other than the one on screen.
      this.onPopState = () => {
        // Any history move cancels a pending update, so a late response can't overwrite the page.
        this.request += 1;
        if (this.controller) this.controller.abort();
        // The cancelled update's own cleanup is skipped (its request token is stale), so clear
        // its busy state here.
        const results = this.querySelector('[data-results]');
        if (results) results.removeAttribute('aria-busy');
        if (window.location.href !== this.renderedUrl) this.navigate(window.location.href, false);
      };

      this.addEventListener('b-filters:change', this.onFilterChange);
      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPopState);
      this.renderedUrl = window.location.href;
      this.request = 0;
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      this.removeEventListener('b-filters:change', this.onFilterChange);
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('popstate', this.onPopState);
    }

    // The new filter query on this page's path.
    urlFor(query) {
      const next = new URL(new URL(window.location.href).pathname, window.location.origin);
      next.search = query;
      return this.keepView(next.toString());
    }

    // An alternate template (?view=) carries over to every in-place URL.
    keepView(url) {
      const next = new URL(url, window.location.origin);
      const view = new URL(window.location.href).searchParams.get('view');
      if (view && !next.searchParams.has('view')) next.searchParams.set('view', view);
      return next.toString();
    }

    announce(text) {
      const status = this.querySelector('[data-status]');
      if (!status) return;
      status.textContent = '';
      window.setTimeout(() => { status.textContent = text; }, 50);
    }

    // Where focus was inside the swapped areas, so the matching control can take it back.
    focusKey() {
      const el = document.activeElement;
      if (!el || !this.contains(el)) return null;
      if (el.name) return { name: el.name, value: el.type === 'checkbox' ? el.value : null };
      const accordion = el.matches('summary') ? el.closest('details[data-filter-key]') : null;
      if (accordion) return { accordion: accordion.dataset.filterKey };
      if (el.closest('b-filters')) return { area: 'filters' };
      if (el.closest('[data-results]')) return { area: 'results' };
      return null;
    }

    restoreFocus(key) {
      if (!key) return;
      let target = null;
      if (key.name) {
        target = [...this.querySelectorAll('.b-filters__form [name]')]
          .find((el) => !el.disabled && el.name === key.name && (key.value === null || el.value === key.value));
      }
      if (key.accordion) {
        const accordion = [...this.querySelectorAll('.b-filters__form details[data-filter-key]')]
          .find((d) => d.dataset.filterKey === key.accordion);
        if (accordion) target = accordion.querySelector(':scope > summary');
      }
      if (!target && key.area !== 'results') {
        // The Filters toggle (it opens and closes the phone drawer).
        target = this.querySelector('.b-filters__panel > summary');
        // Desktop hides the toggle; only a visible control may take focus (it can be
        // position: fixed, so read its boxes rather than offsetParent).
        if (target && !target.getClientRects().length) target = null;
      }
      if (!target) target = this.querySelector('[data-results]');
      // Focus without jumping; a page change scrolls to the top of the products itself.
      if (target) target.focus({ preventScroll: true });
    }

    async fetchSection(url) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const request = new URL(url, window.location.origin);
      request.searchParams.set('section_id', this.sectionId);
      const response = await fetch(request.toString(), { signal: this.controller.signal });
      if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector('b-collection');
      if (!fresh) throw new Error('Section response had no b-collection');
      return fresh;
    }

    swap(fresh, selector) {
      const current = this.querySelector(selector);
      const next = fresh.querySelector(selector);
      if (current && next) current.innerHTML = next.innerHTML;
    }

    async navigate(url, push, toTop = false) {
      const request = ++this.request;
      const results = this.querySelector('[data-results]');
      if (results) results.setAttribute('aria-busy', 'true');
      try {
        const fresh = await this.fetchSection(url);
        if (request !== this.request) return;
        const focus = this.focusKey();
        this.swap(fresh, '[data-results]');
        // The filter accordions the shopper opened stay open after the panel is replaced.
        const open = [...this.querySelectorAll('.b-filters__form details[data-filter-key][open]')].map((d) => d.dataset.filterKey);
        this.swap(fresh, '.b-filters__form');
        this.querySelectorAll('.b-filters__form details[data-filter-key]').forEach((d) => {
          if (open.includes(d.dataset.filterKey)) d.open = true;
        });
        this.swap(fresh, '.b-filters__toggle');
        this.renderedUrl = new URL(url, window.location.origin).toString();
        // Other scripts' history state is kept; this section's lives under its own key.
        if (push) window.history.pushState({ ...(window.history.state || {}), bCollection: { url: this.renderedUrl } }, '', this.renderedUrl);
        const filters = this.querySelector('b-filters');
        if (filters && typeof filters.labelDialog === 'function') filters.labelDialog();
        this.restoreFocus(focus);
        if (toTop && results) {
          const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          results.scrollIntoView({ block: 'start', behavior: still ? 'auto' : 'smooth' });
        }
        const count = this.querySelector('[data-count]');
        if (count) this.announce(count.textContent.trim());
        // One lifecycle event for every change to the cards ('replace'; the Metafield filters panel
        // in assets/b-product-filters.js sends its own).
        // Card code (Custom Liquid, apps) re-initialises from it or uses delegated handlers.
        this.dispatchEvent(new CustomEvent('b-collection:update', { bubbles: true, detail: { url: this.renderedUrl, kind: 'replace', root: this.querySelector('[data-results]') } }));
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.assign(url);
      } finally {
        if (request === this.request) {
          if (results) results.removeAttribute('aria-busy');
        }
      }
    }
  }

  customElements.define('b-collection', BCollection);
}

/*
  Custom element <b-search-more> for blocks/b-search-strip (#584), the search page of
  sections/b-collection (folded in 2026-10-19).

  Fills the "Collections, pages and guides" strip above the search results from Shopify's
  Predictive Search API, rendered by sections/b-search-more (data-url carries the query,
  section_id and resources). Progressive enhancement only: the strip's "See all pages and guides"
  link is in the HTML, so with JavaScript off, a failed request or no matches, the shopper still
  has it and nothing else changes. Filtering, sorting and page links are assets/b-collection.js's.
*/
if (!customElements.get('b-search-more')) {
  class BSearchMore extends HTMLElement {
    connectedCallback() {
      if (this.loading || !this.dataset.url) return;
      this.loading = true;
      this.load(this.dataset.url);
    }

    async load(url) {
      const body = this.querySelector('[data-b-search-more-body]');
      if (!body) return;
      try {
        const response = await fetch(url, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = doc.querySelector('[data-b-search-more-body]');
        // No collections, pages or posts match: the strip keeps only its link.
        if (!fresh || !fresh.querySelector('.b-search-more__cols')) {
          this.setAttribute('data-empty', '');
          return;
        }
        body.replaceChildren(...fresh.childNodes);
        this.setAttribute('data-loaded', '');
      } catch (error) {
        // The server-rendered link stays; the shopper loses nothing but the shortcuts.
        this.setAttribute('data-failed', '');
      }
    }
  }

  customElements.define('b-search-more', BSearchMore);
}
